import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { Plus } from "lucide-react";
import { useLocation } from "wouter";

export default function AddEntityPage() {
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    entityType: "",
    name: "",
    cpfCnpj: "",
    code: "",
    email: "",
    phone: "",
    cellphone: "",
    address: "",
    neighborhood: "",
    city: "",
    state: "",
    cep: "",
    region: "",
    category: "",
    segment: "",
    birthDate: "",
    additionalNotes: "",
  });

  const mutation = useMutation({
    mutationFn: async () => {
      const payload: any = {
        entityType: formData.entityType,
        name: formData.name,
        additionalData: {},
      };

      if (formData.cpfCnpj) payload.cpfCnpj = formData.cpfCnpj;
      if (formData.code) payload.code = formData.code;
      if (formData.email) payload.email = formData.email;
      if (formData.phone) payload.phone = formData.phone;
      if (formData.cellphone) payload.cellphone = formData.cellphone;
      if (formData.address) payload.address = formData.address;
      if (formData.neighborhood) payload.neighborhood = formData.neighborhood;
      if (formData.city) payload.city = formData.city;
      if (formData.state) payload.state = formData.state;
      if (formData.cep) payload.cep = formData.cep;
      if (formData.region) payload.region = formData.region;
      if (formData.category) payload.category = formData.category;
      if (formData.segment) payload.segment = formData.segment;
      if (formData.birthDate) payload.birthDate = formData.birthDate;
      if (formData.additionalNotes) payload.additionalData.notes = formData.additionalNotes;

      const response = await apiRequest("POST", "/api/entities", payload);
      return response.json();
    },
    onSuccess: (data) => {
      toast({
        title: "Sucesso!",
        description: `Entidade #${data.id} criada com sucesso`,
      });
      queryClient.invalidateQueries({ queryKey: ["/api/entities"] });
      queryClient.invalidateQueries({ queryKey: ["/api/stats"] });
      queryClient.invalidateQueries({ queryKey: ["/api/entities/types"] });
      queryClient.invalidateQueries({ queryKey: ["/api/entities/states"] });
      queryClient.invalidateQueries({ queryKey: ["/api/entities/categories"] });
      setTimeout(() => navigate("/entities"), 1000);
    },
    onError: (error: Error) => {
      toast({
        title: "Erro",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.entityType || !formData.name) {
      toast({
        title: "Campos obrigatorios",
        description: "Preencha o tipo de entidade e o nome",
        variant: "destructive",
      });
      return;
    }
    mutation.mutate();
  };

  const estadosBrasileiros = [
    "AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA",
    "MT", "MS", "MG", "PA", "PB", "PR", "PE", "PI", "RJ", "RN",
    "RS", "RO", "RR", "SC", "SP", "SE", "TO"
  ];

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
          <Plus className="w-5 h-5 text-green-400" />
        </div>
        <div>
          <h2 className="text-2xl font-bold" data-testid="text-page-title">Adicionar Novo Cadastro</h2>
          <p className="text-muted-foreground">Cadastre manualmente uma nova entidade</p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <Card>
          <CardHeader>
            <CardTitle>Informacoes da Entidade</CardTitle>
            <CardDescription>
              Preencha os campos abaixo. Campos marcados com * sao obrigatorios.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="entityType">Tipo de Entidade *</Label>
              <Select
                value={formData.entityType}
                onValueChange={(value) =>
                  setFormData({ ...formData, entityType: value })
                }
              >
                <SelectTrigger id="entityType" data-testid="select-entity-type">
                  <SelectValue placeholder="Selecione o tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cadastro">Cadastro</SelectItem>
                  <SelectItem value="cliente">Cliente</SelectItem>
                  <SelectItem value="parceiro">Parceiro</SelectItem>
                  <SelectItem value="outro">Outro</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="name">Nome *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Nome da entidade"
                data-testid="input-name"
              />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="cpfCnpj">CPF/CNPJ</Label>
                <Input
                  id="cpfCnpj"
                  value={formData.cpfCnpj}
                  onChange={(e) => setFormData({ ...formData, cpfCnpj: e.target.value })}
                  placeholder="000.000.000-00 ou 00.000.000/0000-00"
                  data-testid="input-cpf-cnpj"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="code">Codigo</Label>
                <Input
                  id="code"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                  placeholder="Codigo interno"
                  data-testid="input-code"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="email@exemplo.com"
                  data-testid="input-email"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="segment">Segmento</Label>
                <Input
                  id="segment"
                  value={formData.segment}
                  onChange={(e) => setFormData({ ...formData, segment: e.target.value })}
                  placeholder="Ex: Empresário, Pecuarista"
                  data-testid="input-segment"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="birthDate">Data de Nascimento</Label>
                <Input
                  id="birthDate"
                  value={formData.birthDate}
                  onChange={(e) => setFormData({ ...formData, birthDate: e.target.value })}
                  placeholder="dd/mm/aaaa"
                  data-testid="input-birth-date"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="phone">Telefone</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="DDD + Numero"
                  data-testid="input-phone"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cellphone">Celular</Label>
                <Input
                  id="cellphone"
                  value={formData.cellphone}
                  onChange={(e) => setFormData({ ...formData, cellphone: e.target.value })}
                  placeholder="DDD + Numero"
                  data-testid="input-cellphone"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="address">Endereco</Label>
              <Input
                id="address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="Rua, numero, complemento"
                data-testid="input-address"
              />
            </div>

            <div className="grid md:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label htmlFor="neighborhood">Bairro</Label>
                <Input
                  id="neighborhood"
                  value={formData.neighborhood}
                  onChange={(e) => setFormData({ ...formData, neighborhood: e.target.value })}
                  placeholder="Bairro"
                  data-testid="input-neighborhood"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="city">Cidade</Label>
                <Input
                  id="city"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  placeholder="Nome da cidade"
                  data-testid="input-city"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="state">Estado</Label>
                <Select
                  value={formData.state || "none"}
                  onValueChange={(value) =>
                    setFormData({ ...formData, state: value === "none" ? "" : value })
                  }
                >
                  <SelectTrigger id="state" data-testid="select-state">
                    <SelectValue placeholder="UF" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Selecione</SelectItem>
                    {estadosBrasileiros.map((uf) => (
                      <SelectItem key={uf} value={uf}>
                        {uf}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="cep">CEP</Label>
                <Input
                  id="cep"
                  value={formData.cep}
                  onChange={(e) => setFormData({ ...formData, cep: e.target.value })}
                  placeholder="00000000"
                  data-testid="input-cep"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="category">Categoria</Label>
                <Input
                  id="category"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  placeholder="Ex: Produtor Rural"
                  data-testid="input-category"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="region">Regiao</Label>
                <Input
                  id="region"
                  value={formData.region}
                  onChange={(e) => setFormData({ ...formData, region: e.target.value })}
                  placeholder="Ex: Centro-Oeste"
                  data-testid="input-region"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="additionalNotes">Notas Adicionais</Label>
              <Textarea
                id="additionalNotes"
                value={formData.additionalNotes}
                onChange={(e) =>
                  setFormData({ ...formData, additionalNotes: e.target.value })
                }
                placeholder="Informacoes complementares..."
                rows={4}
                data-testid="textarea-notes"
              />
            </div>

            <div className="flex gap-4">
              <Button
                type="submit"
                disabled={mutation.isPending}
                className="flex-1"
                size="lg"
                data-testid="button-submit"
              >
                {mutation.isPending ? "Salvando..." : "Salvar Cadastro"}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate("/entities")}
                data-testid="button-cancel"
              >
                Cancelar
              </Button>
            </div>
          </CardContent>
        </Card>
      </form>
    </div>
  );
}
